/**
 * Scoring for the CRAAP Test.
 *
 * The test itself — Currency, Relevance, Authority, Accuracy, Purpose — is Sarah
 * Blakeslee's, from Meriam Library at California State University, Chico (2004), and it
 * is a list of questions, not a score. The numbers here are this tool's convention for
 * comparing sources, and the page says so:
 *
 * - each guided question is answered Yes (2 points), Partly (1) or No (0); "Not
 *   applicable" drops the question, so a print book is not marked down for broken links;
 * - a criterion scores 0–10 — its points as a share of the points available;
 * - the five criteria add up to a total out of 50, which falls into a band.
 *
 * A total can hide one fatal weakness — a well-written, current, relevant page that is
 * pure advertising — so a criterion below 5 is listed as a weak spot and stops the
 * verdict from reaching the top two bands.
 */

export type Criterion =
  | "currency"
  | "relevance"
  | "authority"
  | "accuracy"
  | "purpose";

export const CRITERIA: readonly Criterion[] = [
  "currency",
  "relevance",
  "authority",
  "accuracy",
  "purpose",
];

export type Rating = "yes" | "partly" | "no" | "na";

export const RATINGS: readonly Rating[] = ["yes", "partly", "no", "na"];

const POINTS: Record<Exclude<Rating, "na">, number> = {
  yes: 2,
  partly: 1,
  no: 0,
};

/** One answer per guided question; `null` until the reader answers it. */
export type CraapAnswers = Record<Criterion, (Rating | null)[]>;

export type Band = "strong" | "usable" | "weak" | "unreliable";

/** The lowest total, out of 50, that reaches each band. */
export const BAND_FLOORS: readonly (readonly [Band, number])[] = [
  ["strong", 40],
  ["usable", 30],
  ["weak", 20],
  ["unreliable", 0],
];

/** A criterion under this score, out of 10, is a weak spot. */
export const WEAK_BELOW = 5;

export interface CriterionScore {
  criterion: Criterion;
  /** 0–10, or null when no question has a scoring answer yet. */
  score: number | null;
  /** Questions answered Yes, Partly or No. */
  scored: number;
  /** Questions answered at all, including Not applicable. */
  answered: number;
  questions: number;
}

export interface CraapResult {
  criteria: CriterionScore[];
  /** Out of 50; null until every criterion has a score. */
  total: number | null;
  band: Band | null;
  /** The band the total alone would give, when a weak spot lowered it. */
  uncappedBand: Band | null;
  weak: Criterion[];
}

export function emptyAnswers(
  counts: Readonly<Record<Criterion, number>>
): CraapAnswers {
  const answers = {} as CraapAnswers;
  for (const criterion of CRITERIA) {
    answers[criterion] = Array.from({ length: counts[criterion] }, () => null);
  }
  return answers;
}

export function scoreCriterion(
  criterion: Criterion,
  answers: readonly (Rating | null)[]
): CriterionScore {
  let points = 0;
  let scored = 0;
  let answered = 0;
  for (const answer of answers) {
    if (answer === null) {
      continue;
    }
    answered += 1;
    if (answer !== "na") {
      points += POINTS[answer];
      scored += 1;
    }
  }
  return {
    criterion,
    score: scored === 0 ? null : (10 * points) / (2 * scored),
    scored,
    answered,
    questions: answers.length,
  };
}

export function bandFor(total: number): Band {
  for (const [band, floor] of BAND_FLOORS) {
    if (total >= floor) {
      return band;
    }
  }
  return "unreliable";
}

const ORDER: readonly Band[] = ["strong", "usable", "weak", "unreliable"];

/** Every criterion's score, the total, and the verdict band. */
export function scoreCraap(answers: CraapAnswers): CraapResult {
  const criteria = CRITERIA.map((criterion) =>
    scoreCriterion(criterion, answers[criterion])
  );
  const weak = criteria
    .filter((c) => c.score !== null && c.score < WEAK_BELOW)
    .map((c) => c.criterion);
  if (criteria.some((c) => c.score === null)) {
    return { criteria, total: null, band: null, uncappedBand: null, weak };
  }
  const total = criteria.reduce((sum, c) => sum + (c.score ?? 0), 0);
  const uncapped = bandFor(total);
  // A weak spot keeps the verdict at "weak" or below.
  const band =
    weak.length > 0 && ORDER.indexOf(uncapped) < ORDER.indexOf("weak")
      ? "weak"
      : uncapped;
  return {
    criteria,
    total,
    band,
    uncappedBand: band === uncapped ? null : uncapped,
    weak,
  };
}
